<template>
    <div
        class="column is-narrow schedule-mat-column"
        :class="{ 'is-drop-target': showScheduleStore.isDraggingCategory && showScheduleStore.editMode }"
        v-if="isMatVisible(matSchedule.id)"
    >
        <div class="is-unselectable schedule-mat-header">
            <span class="has-text-weight-bold">
                {{ matSchedule.name }}
            </span>
            <span
                v-if="
                    showScheduleStore.selectedItems.size > 0 &&
                    showScheduleStore.activeSelectionContext?.type === 'mat' &&
                    showScheduleStore.activeSelectionContext?.matId === matSchedule.id
                "
                class="tag is-info is-light ml-2"
            >
                {{ showScheduleStore.selectedItems.size }} {{ translate("admin_schedule_manage.selected") }}
                <button class="delete is-small" @click="clearSelection"></button>
            </span>
        </div>

        <div ref="matContent" :class="getPointerStyle" class="schedule-mat-content p-0" data-dragscroll>
            <div class="single-scheduled-category-wrapper">
                <div v-if="sortableList.length === 0" class="mat-drop-zone">
                    {{ translate("admin_schedule_manage.drop_here") }}
                </div>
                <Sortable
                    :key="sortableVersion"
                    :options="dragOptions"
                    :list="sortableList"
                    item-key="id"
                    @start="onDragStart"
                    @add="onAddedToMat($event, matSchedule.id)"
                    @remove="onRemovedFromMat($event, matSchedule.id)"
                    @end="onScheduledCategoryDropped($event, matSchedule.id)"
                    class="p-2"
                    style="position: relative; z-index: 1; flex: 1"
                >
                    <template v-slot:item="{ element: scheduledCategory }">
                        <div
                            v-if="!scheduledCategory.canBeMoved"
                            @mouseenter="onCategoryMouseEnter(scheduledCategory.id)"
                            @mouseleave="onCategoryMouseLeave"
                            :class="{
                                'mat-schedule-item': true,
                                'has-warning-outline': isHighlighted(scheduledCategory.id),
                                'is-search-match': isSearchMatch(scheduledCategory.id),
                                'is-hoverable': showScheduleStore.editMode,
                            }"
                            :style="getRowStyle(scheduledCategory)"
                            class="box is-disabled is-condensed"
                        >
                            <SingleScheduledCategory :mat-id="matSchedule.id" :disabled="true" :scheduled-category="scheduledCategory" />
                        </div>
                        <div
                            v-else
                            class="box is-condensed mat-schedule-item"
                            :style="getRowStyle(scheduledCategory)"
                            :class="{
                                'is-just-dropped': justDroppedIds.has(scheduledCategory.id),
                                'has-warning-outline': isHighlighted(scheduledCategory.id),
                                'is-selected': isSelected(scheduledCategory.id),
                                'is-sliced': scheduledCategory.assignmentType.value > 1,
                                'is-search-match': isSearchMatch(scheduledCategory.id),
                                'is-hoverable': showScheduleStore.editMode,
                            }"
                            @mouseenter="onCategoryMouseEnter(scheduledCategory.id)"
                            @mouseleave="onCategoryMouseLeave"
                            @click.stop="toggleSelection(scheduledCategory.id, $event)"
                        >
                            <div class="mat-schedule-item-content">
                                <SingleScheduledCategory
                                    :mat-id="matSchedule.id"
                                    :scheduled-category="scheduledCategory"
                                    :disabled="false"
                                />
                            </div>
                        </div>
                    </template>
                </Sortable>
            </div>
        </div>

        <SingleMatScheduleFooter :mat-schedule-summary="matScheduleSummary" />
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, watch, nextTick } from "vue";
    import { handleError } from "../../Common/errorHandler";
    import schedulesApi, { MatScheduleSummary } from "../../Api/schedules";
    import type { NamedScheduledCategory, MatSchedule, MatSchedulesResult } from "../../Api/schedules";
    import { useScheduleMainStore } from "./useScheduleMainStore";
    import { useShowScheduleStore } from "./useShowScheduleStore";
    import { loadVisibleMatsFromLocalStorage } from "./scheduleManageApp";
    import SingleScheduledCategory from "./SingleScheduledCategory.vue";
    import SingleMatScheduleFooter from "./SingleMatScheduleFooter.vue";
    import { Sortable } from "sortablejs-vue3";
    import cloneDeep from "lodash.clonedeep";
    import { translate } from "../../Common/translations";
    import { SortableEvent } from "../../Common/sortableUtils";

    const HIGHLIGHT_COLORS = [
        "dodgerblue",
        "mediumblue",
        "mediumorchid",
        "mediumpurple",
        "purple",
        "palevioletred",
        "chocolate",
        "cadetblue",
        "brown",
        "seagreen",
        "olive",
        "crimson",
        "orangered",
        "rosybrown",
        "goldenrod",
        "deeppink",
        "darkslateblue",
        "teal",
        "limegreen",
    ];

    const props = defineProps<{
        matSchedule: MatSchedule;
    }>();

    const mainStore = useScheduleMainStore();
    const showScheduleStore = useShowScheduleStore();

    // Computed
    const movableScheduledCategories = computed(() => {
        return showScheduleStore.movableScheduledCategories[props.matSchedule.id] || [];
    });

    const matScheduleSummary = computed<MatScheduleSummary>(() => {
        return (
            showScheduleStore.matScheduleSummaries[props.matSchedule.id] || {
                totalCategories: 0,
                totalCompetitors: 0,
                eta: "",
            }
        );
    });

    const blockedScheduledCategories = computed(() => {
        return showScheduleStore.blockedScheduledCategories[props.matSchedule.id] || [];
    });

    /**
     * Locked categories are rendered inside the Sortable too (but are not draggable), so the whole mat accepts drops.
     * Sortable inserts a dropped row after them, and the *Draggable* event indexes below ignore them.
     */
    const sortableList = computed(() => [...blockedScheduledCategories.value, ...movableScheduledCategories.value]);

    const matContent = ref<HTMLElement | null>(null);
    //A category dropped on this mat is recorded in the store (pendingMatScroll) together with the ids the mat had before.
    //Every refresh of the mat's list settles it: rows with new ids are scrolled to and glow, otherwise (failed move) it is just forgotten.
    //Rows are found by id, not by drop index, because the backend decides the final position.
    const justDroppedIds = ref(new Set<number>());
    let justDroppedTimer: ReturnType<typeof setTimeout> | undefined;
    watch(
        movableScheduledCategories,
        list => {
            const pending = showScheduleStore.pendingMatScroll;
            if (!pending || pending.matId !== props.matSchedule.id) {
                return;
            }
            showScheduleStore.pendingMatScroll = null;
            const known = new Set(pending.knownIds);
            const newIds = list.filter(sc => !known.has(sc.id)).map(sc => sc.id);
            if (newIds.length === 0) {
                return;
            }
            markJustDropped(newIds);
            scrollToRow(blockedScheduledCategories.value.length + list.findIndex(sc => sc.id === newIds[0]));
        },
        { flush: "post" },
    );

    //The glow is a class bound to the row id (not set on the DOM element), so it survives a Sortable re-mount (sortableVersion).
    function markJustDropped(ids: number[]) {
        justDroppedIds.value = new Set(ids);
        clearTimeout(justDroppedTimer);
        justDroppedTimer = setTimeout(() => {
            justDroppedIds.value = new Set();
        }, 2500);
    }

    function scrollToRow(rowIndex: number) {
        //Wait a frame so a Sortable re-mount (sortableVersion) triggered by the same drop does not clamp the position.
        nextTick(() => {
            requestAnimationFrame(() => {
                const container = matContent.value;
                if (!container) {
                    return;
                }
                const row = container.querySelectorAll<HTMLElement>(".mat-schedule-item")[rowIndex];
                if (!row) {
                    container.scrollTop = container.scrollHeight;
                    return;
                }
                //Centre the dropped row so it is clearly visible, also when it was already partly in view.
                const offsetFromTop = row.getBoundingClientRect().top - container.getBoundingClientRect().top;
                container.scrollTop += offsetFromTop - (container.clientHeight - row.offsetHeight) / 2;
            });
        });
    }

    function onDragStart() {
        showScheduleStore.pendingMatScroll = null;
        showScheduleStore.isDraggingCategory = true;
    }

    const getPointerStyle = computed(() => {
        return showScheduleStore.multiRowMode || !showScheduleStore.editMode ? "" : "has-pointer-grab";
    });

    const matVisibility = computed(() => showScheduleStore.matVisibility);
    const conflictsMapping = computed(() => showScheduleStore.conflictsMapping);
    const highlightedConflicts = computed(() => showScheduleStore.highlightedConflicts);
    const conflictsThreshold = computed(() => showScheduleStore.conflictsThreshold);
    const matchingScheduledCategoryIds = computed(() => showScheduleStore.matchingScheduledCategoryIds);

    // ===== Sortable.js Event Handling =====

    //Sortable version is used to force re-rendering of Sortable component, it's incremented when the list changes outside of Sortable events
    const sortableVersion = ref(0);
    const dragOptions = computed(() => ({
        group: "matSchedule",
        animation: 150,
        scroll: true,
        forceFallback: true,
        bubbleScroll: true,
        emptyInsertThreshold: 100,
        draggable: ".mat-schedule-item:not(.is-disabled)",
        disabled: !showScheduleStore.editMode,
    }));

    // Selection methods
    function isSelected(scheduledCategoryId: number): boolean {
        return showScheduleStore.isSelected(scheduledCategoryId);
    }

    function toggleSelection(scheduledCategoryId: number, event: MouseEvent) {
        if (!showScheduleStore.editMode) {
            return;
        }

        // Set active context to this mat (clears selection if coming from dropzone or another mat)
        showScheduleStore.setActiveContext("mat", props.matSchedule.id);

        // SHIFT+click: range selection
        if (event.shiftKey) {
            selectRangeFromClosestSelected(scheduledCategoryId);
            return;
        }

        // CTRL+click (or CMD+click on Mac): toggle individual selection
        if (event.ctrlKey || event.metaKey) {
            showScheduleStore.toggleSingleSelection(scheduledCategoryId);
            return;
        }

        // Regular click: clear selection and select only this item
        showScheduleStore.setSingleSelection(scheduledCategoryId);
    }

    function selectRangeFromClosestSelected(targetScheduledCategoryId: number) {
        const list = movableScheduledCategories.value;
        const targetIndex = list.findIndex(sc => sc.id === targetScheduledCategoryId);

        // Handle case where target not found
        if (targetIndex === -1) {
            return;
        }

        // If no items selected, just select the clicked item
        if (showScheduleStore.selectedItems.size === 0) {
            showScheduleStore.setSingleSelection(targetScheduledCategoryId);
            return;
        }

        // Find all currently selected indices
        const selectedIndices = list
            .map((sc, index) => ({ id: sc.id, index }))
            .filter(item => showScheduleStore.isSelected(item.id))
            .map(item => item.index);

        // Find the closest selected index to the target
        const closestIndex = selectedIndices.reduce((closest, current) => {
            const currentDistance = Math.abs(current - targetIndex);
            const closestDistance = Math.abs(closest - targetIndex);
            return currentDistance < closestDistance ? current : closest;
        });

        // Select range from closest selected item to target
        const minIndex = Math.min(closestIndex, targetIndex);
        const maxIndex = Math.max(closestIndex, targetIndex);

        const itemsToSelect: number[] = [];
        for (let i = minIndex; i <= maxIndex; i++) {
            itemsToSelect.push(list[i].id);
        }

        showScheduleStore.selectRange(itemsToSelect);
    }

    function clearSelection() {
        showScheduleStore.clearSelection();
    }

    function getSelectedScheduledCategories(): NamedScheduledCategory[] {
        return movableScheduledCategories.value.filter(sc => showScheduleStore.isSelected(sc.id));
    }

    /**
     * Handles the @add event when a category is added to this mat from another mat or from DropZone.
     */
    function onAddedToMat(event: SortableEvent, matId: number) {
        //newDraggableIndex is computed with the *source* list's `draggable` option (the drop zone has none), so it may count
        //the locked rows. newIndex counts every row and locked rows always come first, which gives the movable-only index.
        showScheduleStore.matAdded = { matId, newIndex: event.newIndex - blockedScheduledCategories.value.length };
        showScheduleStore.isDraggingCategory = false;
        showScheduleStore.pendingMatScroll = {
            matId,
            knownIds: movableScheduledCategories.value.map(sc => sc.id),
        };

        // Check if coming from DropZone - if so, let DropZone handle the API call
        if (showScheduleStore.dropZoneRemoved.dropZoneItems.length > 0) {
            // Just update sortable version, DropZone will handle the API call and refresh
            sortableVersion.value++;
            return Promise.resolve();
        }

        // Otherwise, it's a mat-to-mat move
        return updateCategoryMoveFromMatToMat().finally(() => {
            sortableVersion.value++;
        });
    }

    /**
     * Handles the @remove event when a category (or multiple selected categories) is removed from this mat to another mat or to DropZone.
     */
    function onRemovedFromMat(event: SortableEvent, matId: number) {
        const draggedCategory = movableScheduledCategories.value[event.oldDraggableIndex];
        if (!draggedCategory) {
            //DOM and list went out of sync; rebuild the Sortable from the store instead of moving a wrong row.
            sortableVersion.value++;
            return Promise.resolve();
        }
        let categoriesToMove: NamedScheduledCategory[] = [];

        // If there are selected items and the dragged item is among them, move all selected items
        if (showScheduleStore.selectedItems.size > 0 && showScheduleStore.isSelected(draggedCategory.id)) {
            categoriesToMove = getSelectedScheduledCategories();
        } else {
            // Otherwise, just move the single dragged item
            categoriesToMove = [draggedCategory];
        }

        showScheduleStore.matRemoved = {
            matId,
            oldIndex: event.oldDraggableIndex,
            scheduledCategoryIds: categoriesToMove.map(sc => sc.id),
        };

        // Checking move to dropzone. If this event arrives as a second one, then let's trigger removal here.
        if (showScheduleStore.dropZoneAdded.newIndex !== null) {
            return removeFromMatToDropZone().then(() => {
                sortableVersion.value++;
            });
        }

        // Otherwise, it's a mat-to-mat move
        return updateCategoryMoveFromMatToMat().finally(() => {
            sortableVersion.value++;
        });
    }

    /**
     * Sortable "end" event handler.
     */
    function onScheduledCategoryDropped(event: SortableEvent, matId: number) {
        showScheduleStore.isDraggingCategory = false;

        // See: updateCategoryMoveFromMatToMat. Don't update if request is coming from
        // non-@end event.
        if (showScheduleStore.matRemoved.matId === matId) {
            showScheduleStore.matRemoved = { matId: 0, oldIndex: null, scheduledCategoryIds: [] };
            return;
        }
        //If it's a move to dropzone from mat, then also ignore reordering
        if (showScheduleStore.dropZoneAdded.newIndex !== null) {
            //Events will be cleared elsewhere.
            return;
        }

        const scheduledCategories = cloneDeep(showScheduleStore.movableScheduledCategories[matId]);
        const item = scheduledCategories[event.oldDraggableIndex];
        scheduledCategories.splice(event.oldDraggableIndex, 1);
        scheduledCategories.splice(event.newDraggableIndex, 0, item);
        showScheduleStore.movableScheduledCategories[matId] = scheduledCategories;

        return updateOrderOfMat(matId);
    }

    function removeFromMatToDropZone() {
        if (showScheduleStore.dropZoneAdded.newIndex === null) {
            return Promise.resolve();
        }

        const matRemoved = showScheduleStore.matRemoved;
        if (!matRemoved.scheduledCategoryIds || matRemoved.scheduledCategoryIds.length === 0) {
            return Promise.resolve();
        }

        const scheduledCategoryIds = matRemoved.scheduledCategoryIds;

        //Clear events before request.
        showScheduleStore.matRemoved = { matId: 0, scheduledCategoryIds: [], oldIndex: null };

        // Remove multiple scheduled categories
        return showScheduleStore
            .removeScheduledCategories(scheduledCategoryIds)
            .then(() => {
                // Clear selection after successful removal
                clearSelection();
            })
            .catch(handleError)
            .finally(() => {
                //Clearing dropzone added event is here to prevent reorder handler in the source mat.
                showScheduleStore.dropZoneAdded = { newIndex: null };
            });
    }

    /**
     * Handles moving one or more categories from one mat to another.
     *
     * Logic: Sortable.js fires @remove and @add events when dragging between mats. This function waits
     * until both events fire (matRemoved and matAdded are set), then removes item(s) from source mat,
     * adds them to target mat, combines each mat's blocked + movable categories, and sends a single bulk
     * update to the backend. On error, refetches all schedules.
     *
     * Note: matRemoved is cleared in onScheduledCategoryDropped() to distinguish cross-mat moves from
     * single-mat reordering.
     */
    function updateCategoryMoveFromMatToMat(): Promise<void> {
        // If either matRemoved or matAdded is not set, we don't need to do anything yet. Both events must happen.
        if (showScheduleStore.matRemoved.matId === 0 || showScheduleStore.matAdded.matId === 0) {
            return Promise.resolve();
        }

        // We need to clear component references ASAP to avoid double request. So let's assign them to local variables.
        const matAdded = showScheduleStore.matAdded;
        const matRemoved = showScheduleStore.matRemoved;
        const scheduledCategoryIdsToMove = matRemoved.scheduledCategoryIds;

        // Mat removed unfortunately must be 0-ed in onScheduledCategoryDropped() method.
        // @end event is required, when order of categories is changed within single mat.
        // If mat Removed is present in @end, it's cancelled and 0-ed.
        showScheduleStore.matAdded = { matId: 0, newIndex: null };

        // Get the items to move
        const itemsToMove = showScheduleStore.movableScheduledCategories[matRemoved.matId].filter(sc =>
            scheduledCategoryIdsToMove.includes(sc.id),
        );

        // Remove items from source mat
        const sourceMatMoveableCategories = cloneDeep(showScheduleStore.movableScheduledCategories[matRemoved.matId]);
        const filteredSourceCategories = sourceMatMoveableCategories.filter(sc => !scheduledCategoryIdsToMove.includes(sc.id));

        // Add items to target mat at the specified position
        const targetMatMoveableCategories = cloneDeep(showScheduleStore.movableScheduledCategories[matAdded.matId]);
        targetMatMoveableCategories.splice(matAdded.newIndex!, 0, ...itemsToMove);

        // Now let's connect blocked scheduled categories and movable ones, and send it to backend.
        const scheduledCategoriesForSource = [
            ...showScheduleStore.blockedScheduledCategories[matRemoved.matId].map(sc => sc.id),
            ...filteredSourceCategories.map(sc => sc.id),
        ];

        const scheduledCategoriesForTarget = [
            ...showScheduleStore.blockedScheduledCategories[matAdded.matId].map(sc => sc.id),
            ...targetMatMoveableCategories.map(sc => sc.id),
        ];

        return mainStore
            .trackRequest(
                schedulesApi.bulkUpdateMatSchedules(
                    mainStore.eventId,
                    mainStore.scheduleId,
                    [
                        {
                            matId: matRemoved.matId,
                            scheduledCategoryIds: scheduledCategoriesForSource,
                        },
                        {
                            matId: matAdded.matId,
                            scheduledCategoryIds: scheduledCategoriesForTarget,
                        },
                    ],
                    conflictsThreshold.value,
                ),
            )
            .then((result: MatSchedulesResult) => {
                const state = result;
                for (const fetchedMatId of Object.keys(state.matSchedules)) {
                    updateStateOfSingleMatSchedule(state, Number(fetchedMatId), true);
                }
                // Clear selection after successful move
                clearSelection();
            })
            .catch(e => {
                handleError(e);
                return fetchMatSchedules();
            });
    }

    /**
     * Ordering of scheduled categories request must have full list of scheduled categories for given mat.
     * It means here it concatenates blocked and movable scheduled categories. Blocked ones are not orderable, but moveable ones are.
     * So whatever is coming from Sortable event, we attach this result to the blocked ones and send full list to backend.
     */
    function updateOrderOfMat(matId: number): Promise<void> {
        const blockedScheduledCategoryIds = (blockedScheduledCategories.value || []).map(scheduledCategory => scheduledCategory.id);

        const movableScheduledCategoryIds = (movableScheduledCategories.value || []).map(scheduledCategory => scheduledCategory.id);

        const scheduledCategoryIds: number[] = [];
        blockedScheduledCategoryIds.forEach(id => scheduledCategoryIds.push(id));
        movableScheduledCategoryIds.forEach(id => scheduledCategoryIds.push(id));

        return mainStore
            .trackRequest(
                schedulesApi.bulkUpdateMatSchedules(
                    mainStore.eventId,
                    mainStore.scheduleId,
                    [
                        {
                            matId: matId,
                            scheduledCategoryIds: scheduledCategoryIds,
                        },
                    ],
                    conflictsThreshold.value,
                ),
            )
            .then((result: MatSchedulesResult) => {
                const state = result;
                for (const fetchedMatId of Object.keys(state.matSchedules)) {
                    updateStateOfSingleMatSchedule(state, Number(fetchedMatId), true);
                }
            })
            .catch(err => {
                handleError(err);
                return fetchMatSchedules();
            });
    }

    // ===== End of Sortable.js Event Handling =====

    // Methods
    function fetchMatSchedules(): Promise<void> {
        const matVisibilityFromLocalStorage = loadVisibleMatsFromLocalStorage(mainStore.eventId) || {};

        return mainStore
            .trackRequest(schedulesApi.getAllMatSchedules(mainStore.eventId, mainStore.scheduleId, [], conflictsThreshold.value))
            .then((result: MatSchedulesResult) => {
                const state = result;
                for (const fetchedMatId of Object.keys(state.matSchedules)) {
                    updateStateOfSingleMatSchedule(state, Number(fetchedMatId), matVisibilityFromLocalStorage[fetchedMatId]);
                }
            })
            .catch(handleError);
    }

    function updateStateOfSingleMatSchedule(state: MatSchedulesResult, matId: number, visible: boolean) {
        showScheduleStore.setConflictedScheduledCategoryIdsFromState(state);
        showScheduleStore.updateStateOfSingleMatSchedule(state, matId, visible);
    }

    function isMatVisible(matId: number): boolean {
        return !!matVisibility.value[matId];
    }

    function getRowStyle(scheduledCategory: NamedScheduledCategory): string {
        if (scheduledCategory.assignmentType.value === 1) {
            return "";
        }

        const colorIndex = scheduledCategory.parameterId % HIGHLIGHT_COLORS.length;

        return "background-color: " + HIGHLIGHT_COLORS[colorIndex] + "; color: white;";
    }

    function onCategoryMouseEnter(scheduledCategoryId: number) {
        highlightConflictsForScheduledCategory(scheduledCategoryId);
    }

    function onCategoryMouseLeave() {
        removeHighlightForScheduledCategory();
    }

    function highlightConflictsForScheduledCategory(scheduledCategoryId: number) {
        if (!conflictsMapping.value[scheduledCategoryId]) {
            return;
        }

        const conflicts = conflictsMapping.value[scheduledCategoryId];

        showScheduleStore.setHighlightedConflicts(conflicts);
    }

    function removeHighlightForScheduledCategory() {
        showScheduleStore.setHighlightedConflicts([]);
    }

    function isHighlighted(scheduledCategoryId: number): boolean {
        return !!highlightedConflicts.value[scheduledCategoryId];
    }

    function isSearchMatch(scheduledCategoryId: number): boolean {
        return !!matchingScheduledCategoryIds.value[scheduledCategoryId];
    }
</script>

<style scoped>
    .draggable-table-item {
        cursor: move;
    }

    .flip-list-move {
        transition: transform 0.5s;
    }

    .ml-2 {
        margin-left: 0.5rem;
    }
</style>
